/*
 * Tennis Racquet Finder - questions and scoring (assets/tennis-racquet-finder-engine.js)
 *
 * Independent of the pickleball kiosk: its own questions, ratings and weights.
 * Plain ES5 so it runs on older kiosk browsers, and loadable in Node for tests
 * (scripts/tennis-finder/engine.test.js). The rubric is documented in
 * TENNIS-FINDER.md; keep the two in step.
 *
 * Inputs per racquet (see snippets/tennis-racquet-finder-product-json.liquid):
 *   specs: measured values from the "tennis" metafields (head_size, length_in,
 *          strung_weight_g, balance_pts_hl, swingweight, stiffness_ra,
 *          string_pattern, beam, spec_source). Missing values stay missing.
 *   storeClass: the store's own racquet collections the product is in
 *          (power, control, spin, comfort, junior, junior-starter, junior-performance).
 * Nothing here invents a spec. Ratings (power, control, ...) are our store's
 * 1-10 ratings DERIVED from the measured specs by the rubric below.
 */
(function (global) {
  "use strict";

  // ---------------------------------------------------------------------
  // Questions. `path` decides which are asked: everyone answers "player";
  // adults then get the adult set, juniors the junior set.
  // ---------------------------------------------------------------------
  var QUESTIONS = [
    { id: "player", path: "all", text: "Who is the racquet for?", options: [
      { id: "new", text: "A beginner", sub: "New to tennis or just getting back into it" },
      { id: "rec", text: "A recreational player", sub: "Plays for fun, rallies comfortably" },
      { id: "club", text: "A club or league player", sub: "Plays regularly, consistent strokes (about 3.5\u20134.0)" },
      { id: "adv", text: "An advanced player", sub: "Competitive, fast full swings (about 4.5+)" },
      { id: "junior", text: "A junior player", sub: "A child about 11 or younger" }
    ] },
    // ---- Adult path ----
    { id: "style", path: "adult", text: "How do you like to play?", options: [
      { id: "baseline", text: "From the baseline", sub: "Long rallies, topspin groundstrokes" },
      { id: "allcourt", text: "All-court", sub: "A bit of everything" },
      { id: "net", text: "At the net / doubles", sub: "Volleys, quick exchanges" },
      { id: "social", text: "Social rallying", sub: "Keep the ball in play and have fun" }
    ] },
    { id: "swing", path: "adult", text: "What does your swing look like?", options: [
      { id: "short", text: "Short and compact", sub: "I want the racquet to help with power" },
      { id: "moderate", text: "Moderate", sub: "A full swing at a medium pace" },
      { id: "long", text: "Long and fast", sub: "I generate my own power" }
    ] },
    { id: "goal", path: "adult", text: "What do you want more of?", options: [
      { id: "power", text: "More power", sub: "Easier depth and pace" },
      { id: "control", text: "More control", sub: "Place the ball where I want" },
      { id: "spin", text: "More spin", sub: "Heavy topspin and sharper angles" },
      { id: "balanced", text: "A balance of everything", sub: "No single priority" }
    ] },
    { id: "weight", path: "adult", text: "What kind of weight feels right?", options: [
      { id: "light", text: "Light and easy to swing", sub: "Under about 300 g strung" },
      { id: "medium", text: "Medium", sub: "About 300\u2013319 g strung" },
      { id: "heavy", text: "Heavier and more stable", sub: "320 g strung and up" },
      { id: "unsure", text: "Not sure", sub: "Suggest a weight for my level" }
    ] },
    { id: "forgive", path: "adult", text: "How important is a big, forgiving sweet spot?", options: [
      { id: "very", text: "Very important", sub: "Help me on off-center hits" },
      { id: "some", text: "Somewhat", sub: "A little help is nice" },
      { id: "not", text: "Not important", sub: "I prefer precision" }
    ] },
    { id: "comfort", path: "adult", text: "What should it feel like at contact?", options: [
      { id: "soft", text: "Soft and comfortable", sub: "A flexible, lower-vibration feel" },
      { id: "some", text: "Comfortable but responsive", sub: "Somewhere in between" },
      { id: "crisp", text: "Crisp and firm", sub: "A stiffer, more direct feel" },
      { id: "none", text: "No preference", sub: "" }
    ] },
    { id: "budget", path: "adult", text: "What's your budget?", options: [
      { id: "b200", text: "Under $200", max: 19999 },
      { id: "b260", text: "Up to $259", max: 25999 },
      { id: "b305", text: "Up to $305", max: 30500 },
      { id: "any", text: "No set budget", max: null }
    ] },
    // ---- Junior path ----
    { id: "height", path: "junior", text: "How tall is the player?", options: [
      { id: "h19", text: "Under 40 in. (3 ft 4 in.)", sub: "About age 4 and under", length: 19 },
      { id: "h21", text: "40\u201344 in.", sub: "About age 4\u20135", length: 21 },
      { id: "h23", text: "45\u201349 in.", sub: "About age 6\u20138", length: 23 },
      { id: "h25", text: "50\u201355 in.", sub: "About age 9\u201310", length: 25 },
      { id: "h26", text: "Over 55 in. (4 ft 7 in.)", sub: "About age 10 and up", length: 26 }
    ] },
    { id: "jlevel", path: "junior", text: "How much does the player play?", options: [
      { id: "start", text: "Just starting", sub: "First racquet or beginner lessons" },
      { id: "regular", text: "Plays regularly", sub: "Weekly lessons or clinics" },
      { id: "compete", text: "Trains or competes", sub: "Tournaments or high-performance programs" }
    ] },
    { id: "jbudget", path: "junior", text: "What's your budget?", options: [
      { id: "j50", text: "Under $50", max: 4999 },
      { id: "j100", text: "Up to $100", max: 10000 },
      { id: "j150", text: "Up to $150", max: 15000 },
      { id: "any", text: "No set budget", max: null }
    ] }
  ];

  var BY_ID = {};
  QUESTIONS.forEach(function (q) { BY_ID[q.id] = q; });

  // The questions asked for a set of answers so far ({questionId: optionId}).
  function questionsFor(answers) {
    var player = answers && answers.player;
    var path = player === "junior" ? "junior" : (player ? "adult" : null);
    return QUESTIONS.filter(function (q) {
      if (q.path === "all") return true;
      if (!path) return q.path === "adult"; // before Q1, show the adult length in the progress bar
      return q.path === path;
    });
  }

  // ---------------------------------------------------------------------
  // Spec helpers
  // ---------------------------------------------------------------------
  function num(v) {
    if (v === null || v === undefined || v === "") return null;
    var n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
    return isNaN(n) ? null : n;
  }
  function clamp(x, lo, hi) { return x < lo ? lo : (x > hi ? hi : x); }
  // Linear 0..10 between x0 and x1.
  function lin(x, x0, x1) { return clamp((x - x0) / (x1 - x0), 0, 1) * 10; }
  function round1(x) { return Math.round(x * 10) / 10; }

  function parsePattern(text) {
    var m = String(text || "").match(/(\d{2})\s*[x\u00d7\/]\s*(\d{2})/i);
    if (!m) return null;
    return { mains: parseInt(m[1], 10), crosses: parseInt(m[2], 10), label: m[1] + "x" + m[2] };
  }

  // Normalised measured specs. Every field may be null.
  function measured(p) {
    var s = p.specs || {};
    var pattern = parsePattern(s.string_pattern);
    return {
      head: num(s.head_size),
      length: num(s.length_in),
      weight: num(s.strung_weight_g),
      balance: num(s.balance_pts_hl), // points head light; negative = head heavy
      swingweight: num(s.swingweight),
      ra: num(s.stiffness_ra),
      pattern: pattern
    };
  }

  function hasClass(p, c) { return (p.storeClass || []).indexOf(c) !== -1; }

  // ---------------------------------------------------------------------
  // Store ratings (1-10) derived from measured specs. See TENNIS-FINDER.md.
  // A rating is null when a spec it needs is missing. The store's own
  // power/control/spin/comfort collections add +1 to that rating (max 10),
  // or give an estimate of 7 when the specs are missing.
  // ---------------------------------------------------------------------
  function ratings(p) {
    if (p._ratings) return p._ratings;
    var m = measured(p);
    var density = m.pattern ? lin(m.pattern.mains * m.pattern.crosses, 280, 360) : null; // 16x18=288 open ... 18x20=360 dense
    var r = {};
    function all() { for (var i = 0; i < arguments.length; i++) if (arguments[i] === null) return false; return true; }

    r.power = all(m.head, m.ra, m.swingweight)
      ? 0.5 * lin(m.head, 95, 110) + 0.3 * lin(m.ra, 58, 72) + 0.2 * lin(m.swingweight, 295, 330) : null;
    r.control = all(m.head, density, m.weight, m.ra)
      ? 0.45 * (10 - lin(m.head, 95, 110)) + 0.25 * density + 0.15 * lin(m.weight, 280, 335) + 0.15 * (10 - lin(m.ra, 58, 72)) : null;
    r.spin = all(density, m.head) ? 0.65 * (10 - density) + 0.35 * lin(m.head, 95, 105) : null;
    r.comfort = all(m.ra, m.weight) ? 0.75 * (10 - lin(m.ra, 56, 72)) + 0.25 * lin(m.weight, 280, 330) : null;
    r.maneuver = all(m.swingweight, m.weight, m.balance)
      ? 0.5 * (10 - lin(m.swingweight, 290, 335)) + 0.3 * (10 - lin(m.weight, 275, 340)) + 0.2 * lin(m.balance, -3, 10) : null;
    r.stability = all(m.weight, m.swingweight) ? 0.5 * lin(m.weight, 275, 340) + 0.5 * lin(m.swingweight, 290, 335) : null;
    r.forgiveness = all(m.head) ? 0.7 * lin(m.head, 95, 110) + 0.3 * (r.stability === null ? 5 : r.stability) : null;

    ["power", "control", "spin", "comfort"].forEach(function (k) {
      var cls = k + "";
      if (hasClass(p, cls)) r[k] = r[k] === null ? 7 : Math.min(10, r[k] + 1);
    });
    Object.keys(r).forEach(function (k) { if (r[k] !== null) r[k] = clamp(round1(r[k]), 1, 10); });
    p._ratings = r;
    return r;
  }

  function weightClass(g) {
    if (g === null) return null;
    if (g < 300) return "light";
    if (g < 320) return "medium";
    return "heavy";
  }
  var WEIGHT_LABEL = { light: "Light", medium: "Medium", heavy: "Heavy" };

  // Suggested player level from head size and strung weight (store rubric).
  function playerLevel(p) {
    if (isJunior(p)) return "Junior";
    var m = measured(p);
    if (m.head === null || m.weight === null) return null;
    if (m.head >= 105 || m.weight < 295) return "Beginner \u2013 intermediate";
    if (m.weight < 310) return "Beginner \u2013 advanced";
    if (m.weight < 322 || m.head > 98) return "Intermediate \u2013 advanced";
    return "Advanced";
  }

  // Junior: in the store's junior collection, or a measured length under 26.5 in.
  function isJunior(p) {
    if (hasClass(p, "junior")) return true;
    var len = num(p.specs && p.specs.length_in);
    return len !== null && len < 26.5;
  }

  // Lengths a junior racquet comes in: the length metafield, else a
  // "Length"/"Size" option whose values are inch lengths (e.g. "21 inch").
  function juniorLengths(p) {
    var len = num(p.specs && p.specs.length_in);
    if (len !== null) return [Math.round(len)];
    var out = [];
    (p.options || []).forEach(function (name, oi) {
      if (!/length|size/i.test(name)) return;
      (p.variants || []).forEach(function (v) {
        var val = v.options && v.options[oi];
        var mm = String(val || "").match(/^\s*(19|21|23|25|26)\s*(?:"|in|inch|\u201d)/i);
        if (mm && out.indexOf(parseInt(mm[1], 10)) === -1) out.push(parseInt(mm[1], 10));
      });
    });
    return out;
  }

  // The variant matching a junior length (for products sold in several lengths).
  function variantForLength(p, length) {
    var found = null;
    (p.options || []).forEach(function (name, oi) {
      if (found || !/length|size/i.test(name)) return;
      (p.variants || []).forEach(function (v) {
        if (found) return;
        var mm = String((v.options && v.options[oi]) || "").match(/^\s*(\d{2})\s*(?:"|in|inch|\u201d)/i);
        if (mm && parseInt(mm[1], 10) === length && v.available) found = v;
      });
    });
    return found;
  }

  // Complete = every measured spec the scoring uses is present.
  function isComplete(p) {
    var m = measured(p);
    return m.head !== null && m.weight !== null && m.balance !== null && m.swingweight !== null && m.ra !== null && m.pattern !== null;
  }

  // ---------------------------------------------------------------------
  // Matching helpers (0..1)
  // ---------------------------------------------------------------------
  // 1 inside [lo, hi]; falls to 0 at `soft` beyond either end.
  function rangeFit(x, lo, hi, soft) {
    if (x === null) return null;
    if (x >= lo && x <= hi) return 1;
    var d = x < lo ? lo - x : x - hi;
    return clamp(1 - d / soft, 0, 1);
  }
  function high(r) { return r === null ? null : r / 10; }
  function low(r) { return r === null ? null : (10 - r) / 10; }
  function mid(r, target) { return r === null ? null : clamp(1 - Math.abs(r - target) / 5, 0, 1); }
  function avg() {
    var sum = 0, n = 0;
    for (var i = 0; i < arguments.length; i++) if (arguments[i] !== null) { sum += arguments[i]; n++; }
    return n ? sum / n : null;
  }

  // Ideal strung weight (g) and head size (sq in) ranges by level.
  var LEVEL_TARGETS = {
    "new": { weight: [270, 300], head: [102, 115] },
    rec: { weight: [285, 312], head: [100, 110] },
    club: { weight: [300, 326], head: [98, 102] },
    adv: { weight: [315, 345], head: [95, 100] }
  };
  var WEIGHT_TARGETS = { light: [265, 299], medium: [300, 319], heavy: [320, 350] };

  // Component weights (sum 100). Independent of the pickleball quiz.
  var WEIGHTS = { level: 20, goal: 20, swing: 13, weight: 12, style: 10, forgive: 10, comfort: 10, budget: 5 };

  var LEVEL_NAME = { "new": "a beginner", rec: "a recreational player", club: "a club player", adv: "an advanced player" };

  // ---------------------------------------------------------------------
  // Guards: racquets we never recommend for an answer set, whatever the score.
  // Returns a reason string, or null when the racquet may be recommended.
  // ---------------------------------------------------------------------
  function guard(p, a) {
    if (isJunior(p)) return "junior racquet";
    var m = measured(p);
    if ((a.player === "new" || a.player === "rec") && a.weight !== "heavy") {
      if (m.weight !== null && m.weight > 325) return "too heavy for this level";
      if (m.head !== null && m.head < 98) return "head too small for this level";
    }
    if (a.player === "new" && m.weight !== null && m.weight >= 320 && a.weight !== "heavy") return "too heavy for a beginner";
    if (a.player === "adv" && a.weight !== "light") {
      if (m.weight !== null && m.weight < 285) return "too light for this level";
      if (m.head !== null && m.head >= 107) return "oversize head for this level";
    }
    if (a.weight === "light" && m.weight !== null && m.weight >= 325) return "much heavier than requested";
    if (a.weight === "heavy" && m.weight !== null && m.weight < 290) return "much lighter than requested";
    if (a.comfort === "soft" && m.ra !== null && m.ra >= 70) return "very stiff frame";
    return null;
  }

  // ---------------------------------------------------------------------
  // Adult scoring
  // ---------------------------------------------------------------------
  function scoreAdult(p, a) {
    var r = ratings(p);
    var m = measured(p);
    var parts = {};
    var lt = LEVEL_TARGETS[a.player] || LEVEL_TARGETS.rec;

    parts.level = avg(rangeFit(m.weight, lt.weight[0], lt.weight[1], 25), rangeFit(m.head, lt.head[0], lt.head[1], 6));

    var wt = a.weight && WEIGHT_TARGETS[a.weight] ? WEIGHT_TARGETS[a.weight] : lt.weight;
    parts.weight = rangeFit(m.weight, wt[0], wt[1], 18);

    if (a.swing === "short") parts.swing = avg(high(r.power), high(r.forgiveness));
    else if (a.swing === "long") parts.swing = avg(high(r.control), high(r.stability));
    else parts.swing = avg(mid(r.power, 5.5), mid(r.control, 5.5));

    if (a.goal === "power") parts.goal = high(r.power);
    else if (a.goal === "control") parts.goal = high(r.control);
    else if (a.goal === "spin") parts.goal = high(r.spin);
    else parts.goal = r.power === null || r.control === null ? null
      : clamp(1 - Math.abs(r.power - r.control) / 6, 0, 1) * 0.6 + avg(mid(r.power, 6), mid(r.control, 6), mid(r.spin, 6)) * 0.4;

    if (a.style === "baseline") parts.style = avg(high(r.spin), high(r.stability));
    else if (a.style === "net") parts.style = avg(high(r.maneuver), high(r.control));
    else if (a.style === "social") parts.style = avg(high(r.comfort), high(r.forgiveness), high(r.maneuver));
    else parts.style = avg(mid(r.maneuver, 6), mid(r.stability, 6), mid(r.power, 6));

    if (a.forgive === "very") parts.forgive = high(r.forgiveness);
    else if (a.forgive === "not") parts.forgive = high(r.control);
    else parts.forgive = mid(r.forgiveness, 6);

    if (a.comfort === "soft") parts.comfort = high(r.comfort);
    else if (a.comfort === "crisp") parts.comfort = low(r.comfort);
    else if (a.comfort === "some") parts.comfort = mid(r.comfort, 6.5);
    else parts.comfort = 1;

    parts.budget = budgetFit(p, a.budget, "budget");

    // Missing data: a part we cannot judge counts as a neutral 0.5, and the
    // racquet's total is scaled by a confidence factor so fully specified
    // racquets win close calls.
    var total = 0, known = 0, possible = 0;
    Object.keys(WEIGHTS).forEach(function (k) {
      var w = WEIGHTS[k];
      possible += w;
      if (parts[k] === null || parts[k] === undefined) { total += 0.5 * w; }
      else { total += parts[k] * w; known += w; }
    });
    var confidence = known / possible;
    var score = total * (0.7 + 0.3 * confidence);
    return { score: round1(score), parts: parts, confidence: confidence };
  }

  function budgetFit(p, answerId, qid) {
    var opt = optionById(qid, answerId);
    if (!opt || opt.max === null || opt.max === undefined) return 1;
    return p.price <= opt.max ? 1 : 0;
  }
  function budgetMax(qid, answerId) {
    var opt = optionById(qid, answerId);
    return opt && opt.max !== null && opt.max !== undefined ? opt.max : null;
  }
  function optionById(qid, id) {
    var q = BY_ID[qid];
    if (!q) return null;
    for (var i = 0; i < q.options.length; i++) if (q.options[i].id === id) return q.options[i];
    return null;
  }

  // ---------------------------------------------------------------------
  // Explanations: built from the parts that helped most, with real numbers.
  // ---------------------------------------------------------------------
  function fmt(n) { return String(Math.round(n)); }

  function explainAdult(p, a, result) {
    var m = measured(p);
    var r = ratings(p);
    var parts = result.parts;
    var reasons = [];
    function add(key, text) { if (text && parts[key] !== null && parts[key] !== undefined) reasons.push({ key: key, gain: parts[key] * WEIGHTS[key], text: text }); }

    if (m.head !== null && m.weight !== null) add("level", "Its " + fmt(m.head) + " sq in head and " + fmt(m.weight) + " g strung weight suit " + (LEVEL_NAME[a.player] || "your level") + ".");
    if (m.weight !== null && a.weight && a.weight !== "unsure") add("weight", WEIGHT_LABEL[weightClass(m.weight)] + " at " + fmt(m.weight) + " g strung, close to the weight you asked for.");
    if (a.goal === "power" && r.power !== null) add("goal", "Power rating " + fmt(r.power) + "/10" + (m.ra !== null ? ", helped by its " + fmt(m.ra) + " RA frame stiffness" : "") + ".");
    if (a.goal === "control" && r.control !== null) add("goal", "Control rating " + fmt(r.control) + "/10" + (m.pattern ? " from its " + m.pattern.label + " string pattern" : "") + (m.head !== null ? " and " + fmt(m.head) + " sq in head" : "") + ".");
    if (a.goal === "spin" && r.spin !== null) add("goal", "Spin rating " + fmt(r.spin) + "/10" + (m.pattern ? ": its " + m.pattern.label + " string pattern is " + (m.pattern.mains * m.pattern.crosses <= 288 ? "open and grips the ball" : (m.pattern.mains * m.pattern.crosses <= 304 ? "a spin-friendly standard" : "on the denser side")) : "") + ".");
    if (a.goal === "balanced" && r.power !== null && r.control !== null) add("goal", "Balanced: power " + fmt(r.power) + "/10 and control " + fmt(r.control) + "/10.");
    if (a.swing === "short" && r.power !== null) add("swing", a.goal === "power" ? "Helps a compact swing hit deep with less effort." : "Gives easy depth on a compact swing (power " + fmt(r.power) + "/10).");
    if (a.swing === "long" && m.swingweight !== null) add("swing", "A " + fmt(m.swingweight) + " swingweight keeps it stable and controllable on full, fast swings.");
    if (a.swing === "moderate" && r.power !== null) add("swing", "A middle-of-the-road power level for a moderate swing.");
    if (a.style === "net" && m.swingweight !== null) add("style", "Quick at the net: " + fmt(m.swingweight) + " swingweight" + (m.balance !== null && m.balance > 0 ? ", " + fmt(m.balance) + " pts head light" : "") + ".");
    if (a.style === "baseline" && r.spin !== null) add("style", "Built for baseline rallies: spin " + fmt(r.spin) + "/10" + (r.stability !== null ? ", stability " + fmt(r.stability) + "/10" : "") + ".");
    if (a.style === "social" && r.forgiveness !== null) add("style", "Easy to rally with: forgiving and comfortable.");
    if (a.forgive === "very" && m.head !== null) add("forgive", "A " + fmt(m.head) + " sq in head gives a larger sweet spot for off-center hits.");
    if (a.forgive === "not" && r.control !== null) add("forgive", "Precise rather than oversized, for players who prefer control.");
    if (a.comfort === "soft" && m.ra !== null) add("comfort", "A more flexible frame (" + fmt(m.ra) + " RA stiffness) for a softer feel at contact.");
    if (a.comfort === "crisp" && m.ra !== null) add("comfort", "A firmer " + fmt(m.ra) + " RA frame for a crisp, direct feel.");

    reasons.sort(function (x, y) { return y.gain - x.gain; });
    var seen = {};
    var out = [];
    for (var i = 0; i < reasons.length && out.length < 3; i++) {
      if (seen[reasons[i].key]) continue;
      seen[reasons[i].key] = true;
      out.push(reasons[i].text);
    }
    if (!isComplete(p)) out.push("Some specs aren't on file for this racquet yet, so ask a team member to confirm the fit.");
    return out.join(" ");
  }

  // ---------------------------------------------------------------------
  // Recommend
  // ---------------------------------------------------------------------
  // Identical measured specs = the same frame in another colorway or edition.
  function frameKey(p) {
    var m = measured(p);
    if (!isComplete(p)) return "title:" + String(p.title || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    return [m.head, m.weight, m.balance, m.swingweight, m.ra, m.pattern.label, m.length].join("|");
  }

  var LABELS = ["Best Match", "Strong Match", "Alternative"];

  function recommend(answers, products, options) {
    options = options || {};
    var max = options.maxRecommendations || 3;
    var minScore = typeof options.minScore === "number" ? options.minScore : 50;
    if (answers.player === "junior") return recommendJunior(answers, products, max);

    var goalKey = { power: "power", control: "control", spin: "spin" }[answers.goal] || null;
    var cap = budgetMax("budget", answers.budget);
    var scored = [];
    var rejected = [];
    products.forEach(function (p) {
      var why = guard(p, answers);
      if (why) { rejected.push({ product: p, reason: why }); return; }
      var res = scoreAdult(p, answers);
      scored.push({ product: p, score: res.score, parts: res.parts, confidence: res.confidence, overBudget: cap !== null && p.price > cap });
    });

    // Ties: higher score, then the goal rating, then complete specs, then available, then lower price, then name.
    scored.sort(function (x, y) {
      if (y.score !== x.score) return y.score - x.score;
      if (goalKey) {
        var gx = ratings(x.product)[goalKey], gy = ratings(y.product)[goalKey];
        if ((gy || 0) !== (gx || 0)) return (gy || 0) - (gx || 0);
      }
      var cx = isComplete(x.product) ? 1 : 0, cy = isComplete(y.product) ? 1 : 0;
      if (cy !== cx) return cy - cx;
      if (!!y.product.available !== !!x.product.available) return y.product.available ? 1 : -1;
      if (x.product.price !== y.product.price) return x.product.price - y.product.price;
      return String(x.product.title).localeCompare(String(y.product.title));
    });

    // In budget first. Over-budget racquets fill empty places only when they
    // are within 15% of the budget, and are labeled as such.
    var picks = [];
    var frames = {};
    function take(list, allowOver) {
      for (var i = 0; i < list.length && picks.length < max; i++) {
        var s = list[i];
        if (s.score < minScore) continue;
        if (s.overBudget && (!allowOver || s.product.price > cap * 1.15)) continue;
        var key = frameKey(s.product);
        if (frames[key]) continue;
        frames[key] = true;
        picks.push(s);
      }
    }
    take(scored.filter(function (s) { return !s.overBudget; }), false);
    if (picks.length < max) take(scored.filter(function (s) { return s.overBudget; }), true);

    picks.forEach(function (s, i) {
      s.rank = i;
      s.best = i === 0;
      s.label = LABELS[i] || "Alternative";
      s.explanation = explainAdult(s.product, answers, s);
      if (s.overBudget) s.explanation += " It's a little above your budget.";
    });
    return { picks: picks, path: "adult", scored: scored, rejected: rejected, profile: adultProfile(answers) };
  }

  function adultProfile(a) {
    var lt = LEVEL_TARGETS[a.player] || LEVEL_TARGETS.rec;
    var wt = a.weight && WEIGHT_TARGETS[a.weight] ? WEIGHT_TARGETS[a.weight] : lt.weight;
    var goal = { power: "Power", control: "Control", spin: "Spin", balanced: "All-round" }[a.goal] || "All-round";
    return [
      { k: "Player", v: optionText("player", a.player) },
      { k: "Focus", v: goal },
      { k: "Head size", v: lt.head[0] + "\u2013" + (lt.head[1] >= 110 ? lt.head[1] + "+" : lt.head[1]) + " sq in" },
      { k: "Strung weight", v: wt[0] + "\u2013" + wt[1] + " g" },
      { k: "Feel", v: { soft: "Soft", some: "Comfortable", crisp: "Crisp", none: "Any" }[a.comfort] || "Any" }
    ];
  }

  function optionText(qid, id) {
    var o = optionById(qid, id);
    return o ? o.text.replace(/^An? /, "").replace(/^\w/, function (c) { return c.toUpperCase(); }) : "";
  }

  // ---------------------------------------------------------------------
  // Junior: fitted by length first (height chart in TENNIS-FINDER.md).
  // ---------------------------------------------------------------------
  var JWEIGHTS = { length: 60, level: 25, budget: 15 };

  function recommendJunior(a, products, max) {
    var hOpt = optionById("height", a.height);
    var want = hOpt ? hOpt.length : null;
    var cap = budgetMax("jbudget", a.jbudget);
    var sizes = [19, 21, 23, 25, 26];
    var scored = [];
    var rejected = [];
    products.forEach(function (p) {
      if (!isJunior(p)) return;
      var lengths = juniorLengths(p);
      if (!lengths.length || want === null) { rejected.push({ product: p, reason: "length not listed" }); return; }
      var exact = lengths.indexOf(want) !== -1;
      // One size longer is a common "grow into it" choice; one size shorter is not offered.
      var next = sizes[sizes.indexOf(want) + 1];
      var up = !exact && next && lengths.indexOf(next) !== -1;
      if (!exact && !up) { rejected.push({ product: p, reason: "wrong length" }); return; }
      var parts = {};
      parts.length = exact ? 1 : 0.35;
      var starter = hasClass(p, "junior-starter"), perf = hasClass(p, "junior-performance");
      if (a.jlevel === "start") parts.level = starter ? 1 : (perf ? 0.3 : 0.6);
      else if (a.jlevel === "compete") parts.level = perf ? 1 : (starter ? 0.1 : 0.5);
      else parts.level = perf ? 0.8 : (starter ? 0.7 : 0.6);
      parts.budget = cap === null || p.price <= cap ? 1 : 0;
      var score = 0;
      Object.keys(JWEIGHTS).forEach(function (k) { score += parts[k] * JWEIGHTS[k]; });
      scored.push({ product: p, score: round1(score), parts: parts, length: exact ? want : next, exact: exact, overBudget: parts.budget === 0 });
    });
    scored.sort(function (x, y) {
      if (y.score !== x.score) return y.score - x.score;
      if (!!y.product.available !== !!x.product.available) return y.product.available ? 1 : -1;
      if (x.product.price !== y.product.price) return x.product.price - y.product.price;
      return String(x.product.title).localeCompare(String(y.product.title));
    });
    var picks = [];
    var titles = {};
    function take(list) {
      for (var i = 0; i < list.length && picks.length < max; i++) {
        var s = list[i];
        if (s.overBudget && (cap === null || s.product.price > cap * 1.15)) continue;
        var key = frameKey(s.product);
        if (titles[key]) continue;
        titles[key] = true;
        picks.push(s);
      }
    }
    take(scored.filter(function (s) { return !s.overBudget; }));
    if (picks.length < max) take(scored.filter(function (s) { return s.overBudget; }));
    picks.forEach(function (s, i) {
      s.rank = i;
      s.best = i === 0;
      s.label = LABELS[i] || "Alternative";
      var bits = [];
      bits.push(s.exact
        ? "The " + s.length + "-inch length matches a player " + hOpt.text.replace(/\s*\(.*\)/, "").replace(/^Under/, "under").replace(/^Over/, "over") + " tall (" + hOpt.sub.toLowerCase() + ")."
        : "One size up (" + s.length + " in.), so there's room to grow. Have a team member check the fit.");
      if (hasClass(s.product, "junior-performance")) bits.push("From our junior performance range, for players in regular lessons or competition.");
      else if (hasClass(s.product, "junior-starter")) bits.push("From our junior starter range, picked for new players.");
      if (s.overBudget) bits.push("It's a little above your budget.");
      s.explanation = bits.join(" ");
      s.variant = variantForLength(s.product, s.length);
    });
    return {
      picks: picks, path: "junior", scored: scored, rejected: rejected,
      profile: [
        { k: "Player", v: "Junior" },
        { k: "Height", v: hOpt ? hOpt.text : "" },
        { k: "Racquet length", v: want ? want + " in." : "" },
        { k: "Level", v: optionText("jlevel", a.jlevel) }
      ]
    };
  }

  var api = {
    QUESTIONS: QUESTIONS,
    questionsFor: questionsFor,
    recommend: recommend,
    ratings: ratings,
    measured: measured,
    weightClass: weightClass,
    playerLevel: playerLevel,
    isJunior: isJunior,
    isComplete: isComplete,
    juniorLengths: juniorLengths,
    variantForLength: variantForLength,
    guard: guard,
    WEIGHTS: WEIGHTS,
    JWEIGHTS: JWEIGHTS
  };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else global.TRFinderEngine = api;
})(this);
